<template>
  <div class="github-pull-request-list">
    <Card
      v-for="pr in pullRequests"
      :key="pr.id"
      class="github-pull-request-card shadow-2"
      :class="pr.url.includes('frontend') ? 'frontend' : 'backend'"
    >
      <template #title>
        <span class="github-pull-request-title">
          Pull-Request ({{ pr.url.includes("frontend") ? "Frontend" : "Backend" }}) ({{
            stateResolver(pr)
          }})
        </span>
      </template>
      <template #content>
        <div class="github-pull-request-content">
          <LISAButton link class="github-pull-request-link" @click="goto(pr.url)">
            Show Pull-Request
          </LISAButton>
          <div class="github-pull-request-user">
            <Avatar :image="pr.user.avatar_url" size="small" shape="circle"></Avatar>
            <span>@{{ pr.user.login }}</span>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
  type GithubPullRequestQuery,
  PullRequestState,
  useGithubPullRequestQuery,
} from "../../../graphql";
import Card from "primevue/card";
import Avatar from "primevue/avatar";
import LISAButton from "../../../components/shared/primevue/LISAButton.vue";
import { isIntern } from "@/functions/environment";
import { useTranslation } from "@/language/translation";

const props = defineProps({
  taskId: {
    type: Number,
    required: true,
  },
});

const { t } = useTranslation();

const pullRequests = ref<GithubPullRequestQuery["GithubPullRequest"]>([]);

const { onResult } = useGithubPullRequestQuery(() => ({
  fetchPolicy: "cache-only",
  enabled: isIntern(),
}));

function stateResolver(pr: GithubPullRequestQuery["GithubPullRequest"][0]) {
  if (pr.state === PullRequestState.open) {
    return "Open";
  }
  if (pr.merged_at != null) {
    return "Merged";
  }
  if (pr.closed_at != null) {
    return "Closed";
  }
  return t("global.na");
}

function goto(url: string) {
  const uri = new URL(url);
  uri.hostname = "github.com";
  uri.pathname = uri.pathname.replace("/repos", "");
  uri.pathname = uri.pathname.replace("/pulls", "/pull");
  window.open(uri, "_blank");
}

onResult((result) => {
  pullRequests.value = (result?.data?.GithubPullRequest ?? []).filter((pr) => {
    const reg = new RegExp(`T${props.taskId}$`, "gm");
    return pr.body != null && pr.body.match(reg);
  });
});
</script>

<style lang="scss" scoped>
.github-pull-request-list {
  display: flex;
  gap: 0.5rem;
  margin-right: 1em;
}

.github-pull-request-card {
  flex: 1 1 0;
  margin: 0.25rem 0;
  overflow: hidden;

  &.frontend {
    border-left: 4px solid #65c89b;
  }

  &.backend {
    border-left: 4px solid #ff15b3;
  }

  :deep(.p-card-body) {
    padding: 0.4rem 0.6rem;
    gap: 0.25rem;
  }

  :deep(.p-card-title) {
    line-height: 1.2;
  }

  :deep(.p-card-content) {
    padding: 0;
  }
}

.github-pull-request-title {
  font-size: 0.825rem;
  font-weight: 600;
}

.github-pull-request-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.github-pull-request-link {
  padding: 0;
  font-size: 0.78rem;
}

.github-pull-request-user {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  font-size: 0.78rem;
  white-space: nowrap;
}
</style>
